<script>
    // ===============================
    // IMPORTS
    // ===============================
    import { createEventDispatcher, onMount } from 'svelte';
    import { numberWithDelimiter } from './utils.js';

    import ArrowDownAZ from 'lucide-svelte/icons/arrow-down-a-z';
    import ArrowDownUp from 'lucide-svelte/icons/arrow-down-up';
    import ArrowUpZA from 'lucide-svelte/icons/arrow-up-z-a';
    import Check from 'lucide-svelte/icons/check';
    import ListFilter from 'lucide-svelte/icons/list-filter';
    import Search from 'lucide-svelte/icons/search';
    import X from 'lucide-svelte/icons/x';

    // ===============================
    // COMPONENT PROPS
    // ===============================
    export let header; // The header name for this filter dropdown
    export let headerOptions; // Options for the current header (e.g., sortable, filterable)
    export let filterOptions; // Available filter options for the current column
    export let sortedColumn; // The currently sorted column
    export let sortDirection; // The direction of the current sort ('asc' or 'desc')
    export let selectedFilters = new Set(); // Selected filters (checkboxes) for the current column
    export let checkboxSearchQueries = {}; // Queries for filtering checkboxes
    export let minValue = 0; // Minimum value for range filtering
    export let maxValue = 100; // Maximum value for range filtering
    export let filters; // The current filters applied to the table

    // ===============================
    // LOCAL STATE
    // ===============================
    let dropdown;
    let filterMode = filters[header]?.filterMode ?? 'Between'; //  Filter mode ('Between' or 'Equals')
    let sliderMin = minValue; // Minimum value of the slider
    let sliderMax = maxValue; // Maximum value of the slider
    let sliderLeft, sliderRight;

    // ===============================
    // EVENT DISPATCHER AND LIFECYCLE
    // ===============================
    const dispatch = createEventDispatcher();

    /**
     * Initialize slider values based on existing filters when the component mounts.
     */
    const positionDropdown = () => {
        const tableContainer = dropdown.closest('.table-container');
        const containerRect = tableContainer.getBoundingClientRect();

        const parentElement = dropdown.closest('th');

        if (parentElement && dropdown) {
            const parentRect = parentElement.getBoundingClientRect();

            const dropdownWidth = dropdown.offsetWidth;

            let dropdownLeft = parentRect.left;
            let dropdownTop = parentRect.bottom + window.scrollY;

            // Check if dropdown overflows to the right
            if (parentRect.left + dropdownWidth > containerRect.right) {
                dropdownLeft = containerRect.right - dropdownWidth; // Keep some padding from the edge

                // Set the dropdown's position based on the parent element
                dropdown.style.left = `${dropdownLeft}px`;
                dropdown.style.top = `${dropdownTop}px`;
            } else {
                dropdown.style.left = `${parentRect.left}px`;
                dropdown.style.top = `${dropdownTop}px`;
            }
        }
    };

    onMount(() => {
        positionDropdown();
        sliderMin = filters[header]?.minValue ?? minValue;
        sliderMax = filters[header]?.maxValue ?? maxValue;
    });

    // ===============================
    // REACTIVE STATEMENTS
    // ===============================

    $: {
        sliderLeft = `${((sliderMin - minValue) / (maxValue - minValue)) * 100}%`;
        sliderRight = `${100 - ((sliderMax - minValue) / (maxValue - minValue)) * 100}%`;
        document.documentElement.style.setProperty('--left', sliderLeft);
        document.documentElement.style.setProperty('--right', sliderRight);
    }

    /**
     * Filter the checkbox options based on the search query.
     */
    $: filteredCheckboxOptions = filterOptions.filter((value) =>
        value
            .toString()
            .toLowerCase()
            .includes(checkboxSearchQueries[header] || ''),
    );

    // ===============================
    // EVENT HANDLERS
    // ===============================
    /**
     * Handle changes to the filter mode (Between/Equals).
     * @param {Event} e - The change event from the select element.
     */
    const handleFilterModeChange = (e) => {
        filterMode = e.target.value;
        filters[header] = {
            ...filters[header],
            type: 'range',
            filterMode,
        };

        if (filterMode === 'Between') {
            sliderMax = sliderMin;
        }

        if (filterMode === 'Equals') {
            sliderMax = sliderMin;
        }
    };

    /**
     * Handle search queries for the current column.
     * @param {string} query - The search query.
     */
    const handleSearch = (query) => {
        if (headerOptions[header]?.searchable) {
            dispatch('search', { header, query: query.toLowerCase().trim() });
        }
    };

    /**
     * Handle search queries for checkbox filtering.
     * @param {string} query - The search query for checkboxes.
     */
    const handleCheckboxSearch = (query) => {
        checkboxSearchQueries = { ...checkboxSearchQueries, [header]: query.toLowerCase() };
    };

    /**
     * Handle select/deselect all checkboxes.
     * @param {boolean} checked - Whether the "Select All" checkbox is checked.
     */
    const handleSelectAll = (checked) => {
        if (checked) {
            filterOptions.forEach((option) => selectedFilters.add(option));
        } else {
            selectedFilters.clear();
        }

        filterOptions.forEach((option) => {
            dispatch('filter', { value: option, checked });
        });
    };

    /**
     * Handle changes to filter checkboxes.
     * @param {string} value - The value of the checkbox.
     * @param {boolean} checked - Whether the checkbox is checked.
     */
    const handleFilterChange = (value, checked) => {
        dispatch('filter', { value, checked });
    };

    /**
     * Handle changes to the range sliders and dispatch the updated values.
     */
    const handleRangeChange = () => {
        if (filterMode === 'Between') {
            if (sliderMin > sliderMax) {
                sliderMin = sliderMax;
            } else if (sliderMax < sliderMin) {
                sliderMax = sliderMin;
            }
        }

        dispatch('rangeFilter', {
            header,
            filterMode,
            minValue: sliderMin,
            maxValue: sliderMax,
        });
    };

    /**
     * Dispatch a sort event with the specified order.
     * @param {string} header - The header being sorted.
     * @param {string} direction - The direction of the sort ('asc' or 'desc').
     */
    const toggleSortOrder = (header, direction) => {
        if (headerOptions[header]?.sortable) {
            dispatch('sort', { header, direction });
        }
    };
</script>

<div
    bind:this={dropdown}
    class="filter-menu"
    role="button"
    tabindex="0"
    on:click|stopPropagation
    on:keydown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.click();
        }
    }}
>
    {#if headerOptions[header]?.searchable}
        <!-- Search input for searching column -->
        <div class="search-container">
            <Search />
            <input
                type="text"
                inputmode="search"
                placeholder="Search …"
                value={filters[header]?.query || ''}
                on:input={(e) => handleSearch(e.target.value)}
            />
        </div>
    {/if}

    {#if headerOptions[header]?.filterable}
        <!-- Search input for filtering checkboxes -->
        <div class="search-container">
            <Search />
            <input
                type="text"
                inputmode="search"
                placeholder="Search …"
                on:input={(e) => handleCheckboxSearch(e.target.value)}
            />
        </div>

        <!-- Select/Deselect All Buttons -->
        <div class="select-toggle-container">
            <button
                class="select-toggle-button {selectedFilters.size === filterOptions.length ? 'disabled' : 'active'}"
                on:click|stopPropagation={() => handleSelectAll(true)}
                disabled={selectedFilters.size === filterOptions.length}
            >
                <Check /> Select All
            </button>
            <button
                class="select-toggle-button {selectedFilters.size === 0 ? 'disabled' : 'active'}"
                on:click|stopPropagation={() => handleSelectAll(false)}
                disabled={selectedFilters.size === 0}
            >
                <X /> Clear
            </button>
        </div>

        <ul class="select-options-list">
            <!-- Display checkboxes filtered by the search query -->
            {#each filteredCheckboxOptions as value}
                <li>
                    <label for={header + '-' + value}>
                        <input
                            type="checkbox"
                            id={header + '-' + value}
                            checked={selectedFilters.has(value)}
                            on:change={(e) => handleFilterChange(value, e.target.checked)}
                        />
                        {value}
                    </label>
                </li>
            {/each}
        </ul>
    {/if}

    {#if headerOptions[header]?.sortable}
        <!-- Sort Options (Ascending/Descending) -->
        <span>
            <ArrowDownUp /> Sort:
        </span>
        <div class="sort-toggle-container">
            <div
                class="sort-toggle-background"
                style="transform: translateX({sortDirection == 'asc' && sortedColumn == header ? '0%' : '100%'})"
            ></div>
            <button
                class="sort-toggle-button {sortDirection === 'asc' && sortedColumn === header ? 'active' : ''}"
                on:click|stopPropagation={() => toggleSortOrder(header, 'asc')}
            >
                <ArrowDownAZ /> Ascending
            </button>
            <button
                class="sort-toggle-button {sortDirection === 'desc' && sortedColumn === header ? 'active' : ''}"
                on:click|stopPropagation={() => toggleSortOrder(header, 'desc')}
            >
                <ArrowUpZA /> Descending
            </button>
        </div>

        {#if headerOptions[header]?.filterable}
            <!-- Range Slider with Toggle -->
            <span><ListFilter /> Filter:</span>
            <div>
                <select bind:value={filterMode} on:change={handleFilterModeChange}>
                    <option value="Between">Between</option>
                    <option value="Equals">Equals</option>
                </select>
            </div>

            <!-- Range Input Fields for Sortable Columns -->
            {#if filterMode === 'Between'}
                <!-- Range Input Fields for "Between" Mode -->
                <div class="range-slider-container">
                    <input
                        type="range"
                        min={minValue}
                        max={maxValue}
                        class="range-slider"
                        onmouseup="this.blur()"
                        onmouseleave="this.blur()"
                        bind:value={sliderMin}
                        on:input={handleRangeChange}
                    />
                    <input
                        type="range"
                        min={minValue}
                        max={maxValue}
                        class="range-slider"
                        onmouseup="this.blur()"
                        onmouseleave="this.blur()"
                        bind:value={sliderMax}
                        on:input={handleRangeChange}
                    />
                    <div class="range-track" style="right: {sliderRight}%; left: {sliderLeft}%;"></div>
                </div>
                <div class="range-values">
                    Between
                    {#if headerOptions[header]?.delimiter == false}
                        {sliderMin} and {sliderMax}
                    {:else}
                        {numberWithDelimiter(sliderMin)} and {numberWithDelimiter(sliderMax)}
                    {/if}
                </div>
            {:else if filterMode === 'Equals'}
                <div class="range-slider-container">
                    <input
                        type="range"
                        min={minValue}
                        max={maxValue}
                        class="range-slider"
                        onmouseup="this.blur()"
                        onmouseleave="this.blur()"
                        bind:value={sliderMin}
                        on:change={handleRangeChange}
                    />
                </div>
                <div class="range-values">Equals {sliderMin}</div>
            {/if}
        {/if}
    {/if}
</div>

<style>
    button {
        border: 1px solid #005fcc;
        border-radius: 4px;
        background-color: #005fcc;
        color: #fff;
        font-size: var(--scale-1);
    }

    button:hover {
        border-color: #06aee0;
        background-color: #06aee0;
    }

    input[type='checkbox'] {
        accent-color: #005fcc;
    }

    input[type='text'] {
        width: 100%;
        border: 0;
        border-radius: 4px;
        background-color: var(--gray-3);
    }

    input[type='range'] {
        width: 100%;
        border: 0;
        appearance: none;
    }

    input[type='text']:focus-visible,
    input[type='range']:focus-visible {
        outline: 0;
    }

    select {
        width: 100%;
        border: 0;
        border-radius: 4px;
        background-color: #f6f6f6;
    }

    .filter-menu {
        position: absolute;
        z-index: 999;
        display: block;
        max-height: 300px;
        padding: 1em;
        border: 1px solid #ddd;
        background-color: rgb(255 255 255 / 95%);
        color: initial;
        color: #666;
        cursor: auto;
        font-weight: initial;
        font-weight: normal;
        overflow-y: auto;
        text-align: left;
    }

    .search-container {
        display: flex;
        align-items: center;
        padding: 0;
        padding-left: 10px;
        border-radius: 4px;
        background: var(--gray-3);
        color: initial;
    }

    .search-container:focus-within {
        outline: 2px solid #005fcc;
    }

    .select-toggle-button {
        position: relative;
        z-index: 1;
        width: 100%;
        width: fit-content;
        height: 100%;
        padding: 10px;
        border: none;
        border-radius: 4px;
        background: none;
        background: var(--gray-3);
        color: #666;
        cursor: pointer;
        font-size: 16px;
        outline: none;
        transition: color 0.3s;
    }

    .select-toggle-button:disabled {
        pointer-events: none;
    }

    .select-toggle-button:hover:not(.disabled) {
        background: #e0e0e0;
    }

    .select-toggle-button.disabled {
        border: 1px dashed #999;
        background: transparent;
        color: #999;
        cursor: not-allowed;
    }

    .select-options-list {
        max-height: 200px;
        padding: var(--spacing-1);
        border: 1px solid #888;
        border: 0;
        border-radius: 4px;
        margin: 0;
        background: #f6f6f6;
        list-style-type: none;
        overflow-y: auto;
    }

    .sort-toggle-background {
        position: absolute;
        width: 50%;
        height: 100%;
        border-radius: 4px;
        transition: transform 0.3s ease;
    }

    .select-toggle-container,
    .sort-toggle-container {
        position: relative;
        display: flex;
        overflow: hidden;
        width: 100%;
        height: auto;
        justify-content: space-between;
        border-radius: 4px;
        margin: 1em 0;
        background-color: #f6f6f6;
        user-select: none;
    }

    .select-toggle-container {
        height: auto;
        background: none;
    }

    .sort-toggle-button {
        position: relative; /* Ensure it's above the background element */
        z-index: 1;
        width: 100%; /* Fill the space */
        height: 100%; /* Fill the vertical space */
        flex: 1;
        padding: 10px;
        border: none;
        border-radius: 4px;
        background: none;
        color: #666; /* Darker text for visibility */
        cursor: pointer;
        font-size: 16px;
        outline: none;
        transition: color 0.3s;
        white-space: nowrap;
    }

    .sort-toggle-button:hover {
        background: #e0e0e0;
    }

    .sort-toggle-button:focus {
        border: 2px solid #005fcc;
    }

    .sort-toggle-button.active,
    .sort-toggle-button.active:hover {
        border: 2px solid #005fcc;
        background: #005fcc;
        color: #fff;
        font-weight: 400;
    }

    .range-slider-container {
        position: relative;
        width: 100%;
        height: 40px;
        margin: 10px 0;
    }

    .range-slider {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        appearance: none;
        background: none;
        pointer-events: none;
    }

    /* Thumb styling */
    .range-slider::-webkit-slider-thumb {
        position: relative;
        z-index: 3; /* Ensure the thumb is above the track */
        width: 20px;
        height: 20px;
        border: 2px solid #fff;
        border-radius: 50%;
        margin-top: -8px;
        appearance: none;
        background: #005fcc;
        cursor: grab;
        pointer-events: all;
    }

    .range-slider::-moz-range-thumb {
        position: relative;
        z-index: 3;
        width: 20px;
        height: 20px;
        border: 2px solid #fff;
        border-radius: 50%;
        margin-top: -8px;
        background: #005fcc;
        cursor: grab;
        pointer-events: all;
    }

    .range-slider:focus::-webkit-slider-thumb {
        cursor: grabbing;
        outline: 2px solid #005fcc;
    }

    .range-slider:focus::-moz-range-thumb {
        cursor: grabbing;
        outline: 2px solid #005fcc;
    }

    /* The unfilled track */
    .range-slider::-webkit-slider-runnable-track {
        width: 100%;
        height: 5px;
        border: none;
        border-radius: 5px;
        background: #ddd;
    }

    .range-slider::-moz-range-track {
        width: 100%;
        height: 5px;
        border: none;
        border-radius: 5px;
        background: #ddd;
    }

    /* Custom filled track between the thumbs */
    .range-track {
        position: absolute;
        z-index: 1;
        top: 50%;
        right: var(--right);
        left: var(--left);
        height: 5px;
        border-radius: 5px;
        background: #005fcc;
        pointer-events: none;
        transform: translateY(-50%);
    }

    .range-values {
        margin: 0;
        text-align: center;
    }

    @media (prefers-color-scheme: dark) {
        .filter-menu {
            border-color: var(--cool-90);
            background-color: var(--cool-80);
            color: var(--color-text-body);
        }

        input[type='text'],
        select,
        .search-container,
        .select-toggle-button,
        .select-options-list,
        .sort-toggle-container,
        .sort-toggle-button {
            background-color: var(--cool-70);
            color: var(--color-text-body);
        }

        .select-toggle-button:hover,
        .sort-toggle-button:hover {
            background-color: var(--cool-60);
        }

        .select-toggle-button.disabled {
            border-color: var(--cool-70);
            color: var(--cool-70);
        }

        input[type='text']::placeholder {
            color: var(--color-text-body);
        }

        .range-slider::-webkit-slider-runnable-track {
            background-color: var(--cool-70);
        }
    }
</style>
